import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, RefreshCw, Users, Mail, Phone, MapPin, ArrowLeft, Search, X,
  BarChart3, Clock, Save, Sparkles, Image as ImageIcon, Bot, Copy, Check,
  TrendingUp, Lightbulb, Send, MessageCircle, RotateCw, CheckCheck, LogOut,
  CheckCircle2, AlertTriangle, MailX, Trash2, Archive, ArchiveRestore, CalendarDays,
  Shield, ShieldCheck, RotateCcw, Trash,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { normalizeItalianPhone, isValidEmail, waLinkTo, mailtoTo, EMAIL_SUBJECT } from "../config";

const TONES = ["professionale", "cordiale", "sintetico"];
const TEMPLATES = [
  { key: "prima_risposta", label: "Prima risposta" },
  { key: "richiesta_foto", label: "Richiesta fotografie" },
  { key: "richiesta_misure", label: "Richiesta misure" },
  { key: "proposta_sopralluogo", label: "Proposta sopralluogo" },
  { key: "ricontatto", label: "Ricontatto" },
  { key: "conferma_appuntamento", label: "Conferma appuntamento" },
  { key: "richiesta_dettagli", label: "Richiesta dettagli mancanti" },
];
const ACTIVITY_LABELS = {
  draft_generated: "Bozza generata",
  draft_edited: "Bozza modificata",
  text_copied: "Testo copiato",
  whatsapp_opened: "WhatsApp aperto",
  email_opened: "Email aperta",
  draft_regenerated: "Bozza rigenerata",
  marked_contacted: "Segnato come contattato",
  note: "Nota",
  sopralluogo_reminder: "Promemoria sopralluogo",
};
import { Logo } from "../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_COLORS = {
  "Nuova richiesta": "bg-blue-100 text-blue-700",
  "Da contattare": "bg-amber-100 text-amber-700",
  "Preventivo inviato": "bg-purple-100 text-purple-700",
  "Sopralluogo": "bg-cyan-100 text-cyan-700",
  "Lavoro confermato": "bg-orange-100 text-orange-700",
  "Lavoro concluso": "bg-green-100 text-green-700",
};
const PRIORITY_COLORS = {
  Alta: "bg-brand-orange/15 text-brand-orange",
  Media: "bg-blue-100 text-blue-700",
  Bassa: "bg-gray-100 text-gray-500",
};

const SOURCES = ["Google Ads", "Facebook", "Instagram", "Organico", "Diretto"];
const PRIORITIES = ["Alta", "Media", "Bassa"];

const EMAIL_STATUS = {
  delivered: { label: "Consegnata", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  accepted: { label: "Accettata dal provider", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  blocked_sandbox: { label: "Bloccata (sandbox)", cls: "bg-orange-100 text-orange-700", Icon: Shield },
  rate_limited: { label: "Limite invii raggiunto", cls: "bg-yellow-100 text-yellow-800", Icon: RefreshCw },
  sending: { label: "Invio in corso", cls: "bg-blue-100 text-blue-700", Icon: Loader2, spin: true },
  failed: { label: "Invio fallito", cls: "bg-red-100 text-red-700", Icon: AlertTriangle },
  not_configured: { label: "Email non configurata", cls: "bg-gray-100 text-gray-500", Icon: MailX },
};
// Legacy values stored before the honest-status update.
const EMAIL_STATUS_ALIAS = { sent: "accepted", error: "failed" };
const EMAIL_FILTERS = [
  { value: "delivered", label: "Consegnata" },
  { value: "accepted", label: "Accettata dal provider" },
  { value: "blocked_sandbox", label: "Bloccata (sandbox)" },
  { value: "rate_limited", label: "Limite invii raggiunto" },
  { value: "sending", label: "Invio in corso" },
  { value: "failed", label: "Invio fallito" },
  { value: "not_configured", label: "Email non configurata" },
];

// Single source of truth for status wording (badges, toasts, filters).
function emailStatusLabel(status) {
  const norm = EMAIL_STATUS_ALIAS[status] || status;
  return (EMAIL_STATUS[norm] || {}).label || "Stato sconosciuto";
}

function EmailBadge({ status }) {
  const norm = EMAIL_STATUS_ALIAS[status] || status;
  const cfg = EMAIL_STATUS[norm] || EMAIL_STATUS.sending;
  const { Icon } = cfg;
  return (
    <span data-testid="email-status-badge" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.cls}`}>
      <Icon size={12} className={cfg.spin ? "animate-spin" : ""} /> {cfg.label}
    </span>
  );
}

function sopralluogoStatus(ds, stato) {
  if (!ds || stato === "Lavoro concluso") return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(ds + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { label: "Sopralluogo scaduto", cls: "bg-red-100 text-red-700" };
  if (diff === 0) return { label: "Sopralluogo oggi", cls: "bg-orange-100 text-orange-700" };
  if (diff === 1) return { label: "Sopralluogo domani", cls: "bg-amber-100 text-amber-800" };
  return null;
}

function ConfirmDialog({ state, onOpenChange }) {
  const { open, title, description, confirmLabel = "Conferma", danger, onConfirm, listItems } = state;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {listItems?.length > 0 && (
          <div data-testid="confirm-list" className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-surface p-3 text-sm text-ink/75 space-y-1">
            {listItems.map((it, i) => <p key={i} className="truncate">• {it}</p>)}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-accept"
            onClick={onConfirm}
            className={danger ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function CRM() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [leads, setLeads] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fStato, setFStato] = useState("");
  const [fProv, setFProv] = useState("");
  const [fPrio, setFPrio] = useState("");
  const [fEmail, setFEmail] = useState("");
  const [fZona, setFZona] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fSopralluogo, setFSopralluogo] = useState("");
  const [view, setView] = useState("active"); // active | archived | trash
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });
  const [emailErrors, setEmailErrors] = useState(0);
  const [trashCount, setTrashCount] = useState(0);
  const [trashOldCount, setTrashOldCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [clientsRevenue, setClientsRevenue] = useState(0);
  const [sopralluoghiDue, setSopralluoghiDue] = useState(0);
  const [testingEmail, setTestingEmail] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (fStato) params.stato = fStato;
      if (fProv) params.provenienza = fProv;
      if (fPrio) params.priorita = fPrio;
      if (fEmail) params.email_status = fEmail;
      if (fZona) params.zona = fZona;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (fSopralluogo) params.sopralluogo = fSopralluogo;
      if (view === "trash") params.deleted = true;
      else if (view === "archived") params.archived = true;
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, { params }),
        axios.get(`${API}/leads/stats`),
      ]);
      setLeads(l.data);
      setStates(s.data.states);
      setEmailErrors(s.data.email_errors || 0);
      setTrashCount(s.data.trash_count || 0);
      setTrashOldCount(s.data.trash_old_count || 0);
      setClientsCount(s.data.clients_count || 0);
      setClientsRevenue(s.data.clients_revenue || 0);
      setSopralluoghiDue(s.data.sopralluoghi_due || 0);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [q, fStato, fProv, fPrio, fEmail, fZona, fFrom, fTo, fSopralluogo, view]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    const { data } = await axios.patch(`${API}/leads/${id}`, body);
    applyUpdate(data);
    return data;
  };

  const applyUpdate = (data) => {
    setLeads((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setSelected((s) => (s?.id === data.id ? data : s));
  };

  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === leads.length ? new Set() : new Set(leads.map((l) => l.id))));
  };

  const doArchive = async (lead, archived) => {
    try {
      await axios.post(`${API}/leads/${lead.id}/archive`, { archived });
      setLeads((prev) => prev.filter((x) => x.id !== lead.id));
      setSelected((s) => (s?.id === lead.id ? null : s));
      toast.success(archived ? "Richiesta archiviata" : "Richiesta ripristinata");
    } catch (e) { toast.error("Errore durante l'archiviazione"); }
  };

  const doProtect = async (lead, protectedVal) => {
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/protect`, { protected: protectedVal });
      applyUpdate(data);
      toast.success(protectedVal ? "Lead protetto: non eliminabile" : "Protezione rimossa");
    } catch (e) { toast.error("Errore durante l'aggiornamento della protezione"); }
  };

  const doRestoreSingle = async (lead) => {
    try {
      await axios.post(`${API}/leads/${lead.id}/restore`);
      setLeads((prev) => prev.filter((x) => x.id !== lead.id));
      setSelected((s) => (s?.id === lead.id ? null : s));
      setTrashCount((c) => Math.max(0, c - 1));
      toast.success("Richiesta ripristinata");
    } catch (e) { toast.error("Ripristino non riuscito"); }
  };

  const doPermanentSingle = (lead) => {
    setConfirm({
      open: true, danger: true, confirmLabel: "Elimina per sempre",
      title: "Eliminare definitivamente?",
      description: `"${lead.nome}" sarà eliminato in modo IRREVERSIBILE dal cestino. Questa azione non può essere annullata.`,
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/leads/${lead.id}/permanent`);
          setLeads((prev) => prev.filter((x) => x.id !== lead.id));
          setSelected((s) => (s?.id === lead.id ? null : s));
          setTrashCount((c) => Math.max(0, c - 1));
          toast.success("Eliminato definitivamente");
        } catch (e) { toast.error(e.response?.status === 409 ? "Lead protetto: rimuovi prima la protezione" : "Errore durante l'eliminazione"); }
      },
    });
  };

  const doBulkRestore = async () => {
    const ids = [...selectedIds];
    try {
      const { data } = await axios.post(`${API}/leads/bulk-restore`, { ids });
      setLeads((prev) => prev.filter((x) => !selectedIds.has(x.id)));
      setSelectedIds(new Set());
      setTrashCount((c) => Math.max(0, c - (data.restored || 0)));
      toast.success(`${data.restored} richieste ripristinate`);
    } catch (e) { toast.error("Ripristino non riuscito"); }
  };

  const doBulkPermanent = () => {    const ids = [...selectedIds];
    setConfirm({
      open: true, danger: true, confirmLabel: `Elimina per sempre ${ids.length}`,
      title: `Eliminare definitivamente ${ids.length} richieste?`,
      description: "Le richieste selezionate saranno eliminate in modo IRREVERSIBILE dal cestino. I lead protetti verranno saltati.",
      onConfirm: async () => {
        closeConfirm(); setBusy(true);
        try {
          const { data } = await axios.post(`${API}/leads/bulk-permanent-delete`, { ids });
          setSelectedIds(new Set());
          toast.success(`${data.deleted} eliminate definitivamente${data.skipped_protected ? ` · ${data.skipped_protected} protette saltate` : ""}`);
          load();
        } catch (e) { toast.error("Errore durante l'eliminazione"); }
        finally { setBusy(false); }
      },
    });
  };

  const restoreLead = async (id) => {
    try { await axios.post(`${API}/leads/${id}/restore`); toast.success("Richiesta ripristinata"); load(); }
    catch (e) { toast.error("Ripristino non riuscito"); }
  };
  const restoreBulk = async (ids) => {
    try { await axios.post(`${API}/leads/bulk-restore`, { ids }); toast.success("Richieste ripristinate"); load(); }
    catch (e) { toast.error("Ripristino non riuscito"); }
  };

  const doDeleteSingle = (lead) => {
    if (lead.protected) { toast.error("Lead protetto: rimuovi la protezione prima di eliminarlo"); return; }
    setConfirm({
      open: true, danger: true, confirmLabel: "Elimina",
      title: "Eliminare la richiesta?",
      description: `Stai per spostare nel cestino la richiesta di "${lead.nome}". Sarà recuperabile dal Cestino o con "Annulla".`,
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/leads/${lead.id}`);
          setLeads((prev) => prev.filter((x) => x.id !== lead.id));
          setSelected((s) => (s?.id === lead.id ? null : s));
          setTrashCount((c) => c + 1);
          toast.success("Spostata nel cestino", { duration: 7000, action: { label: "Annulla", onClick: () => restoreLead(lead.id) } });
        } catch (e) { toast.error(e.response?.status === 409 ? "Lead protetto: rimuovi la protezione" : e.response?.status === 403 ? "Solo gli amministratori possono eliminare" : "Errore durante l'eliminazione"); }
      },
    });
  };

  const doBulkDelete = () => {
    const ids = [...selectedIds];
    setConfirm({
      open: true, danger: true, confirmLabel: `Elimina ${ids.length}`,
      title: `Eliminare ${ids.length} richieste?`,
      description: "Le richieste selezionate saranno spostate nel cestino (i lead protetti verranno saltati). Recuperabili con \"Annulla\" o dal Cestino.",
      onConfirm: async () => {
        closeConfirm(); setBusy(true);
        try {
          const { data } = await axios.post(`${API}/leads/bulk-delete`, { ids });
          const removed = new Set(data.ids || []);
          setLeads((prev) => prev.filter((x) => !removed.has(x.id)));
          setSelectedIds(new Set());
          setTrashCount((c) => c + (data.deleted || 0));
          toast.success(`${data.deleted} spostate nel cestino${data.skipped_protected ? ` · ${data.skipped_protected} protette saltate` : ""}`, { duration: 7000, action: { label: "Annulla", onClick: () => restoreBulk(data.ids || ids) } });
        } catch (e) { toast.error(e.response?.status === 403 ? "Solo gli amministratori possono eliminare" : "Errore durante l'eliminazione"); }
        finally { setBusy(false); }
      },
    });
  };

  const doDeleteTest = async () => {
    let preview = { count: 0, leads: [] };
    try { preview = (await axios.get(`${API}/leads/test/preview`)).data; } catch (e) { /* ignore */ }
    if (!preview.count) { toast.info("Nessuna richiesta di test individuata"); return; }
    const ids = preview.leads.map((l) => l.id);
    setConfirm({
      open: true, danger: true, confirmLabel: `Elimina ${preview.count}`,
      title: "Eliminare le richieste di test?",
      description: `Sono state individuate ${preview.count} richieste con criteri restrittivi (email di test o "test" come parola intera nel nome). Controlla l'elenco prima di confermare. Recuperabili con "Annulla".`,
      listItems: preview.leads.map((l) => `${l.nome} — ${l.email}`),
      onConfirm: async () => {
        closeConfirm(); setBusy(true);
        try {
          const { data } = await axios.post(`${API}/leads/delete-test`, { ids });
          toast.success(`${data.deleted} richieste di test eliminate`, { duration: 7000, action: { label: "Annulla", onClick: () => restoreBulk(data.ids || ids) } });
          load();
        } catch (e) { toast.error(e.response?.status === 403 ? "Solo gli amministratori possono eliminare" : "Errore durante l'eliminazione"); }
        finally { setBusy(false); }
      },
    });
  };

  const doEmptyTrash = () => {
    setConfirm({
      open: true, danger: true, confirmLabel: "Svuota cestino",
      title: "Svuotare il cestino?",
      description: `Tutte le richieste nel cestino non protette (${Math.max(trashCount, 0)}) saranno eliminate in modo IRREVERSIBILE. I lead protetti resteranno nel cestino.`,
      onConfirm: async () => {
        closeConfirm(); setBusy(true);
        try {
          const { data } = await axios.post(`${API}/leads/empty-trash`);
          toast.success(`Cestino svuotato · ${data.deleted} eliminate definitivamente`);
          load();
        } catch (e) { toast.error(e.response?.status === 403 ? "Solo gli amministratori" : "Errore durante lo svuotamento"); }
        finally { setBusy(false); }
      },
    });
  };

  const doExport = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (q) params.q = q;
      if (fStato) params.stato = fStato;
      if (fProv) params.provenienza = fProv;
      if (fPrio) params.priorita = fPrio;
      if (fEmail) params.email_status = fEmail;
      if (fZona) params.zona = fZona;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (fSopralluogo) params.sopralluogo = fSopralluogo;
      if (view === "trash") params.deleted = true;
      else if (view === "archived") params.archived = true;
      const res = await axios.get(`${API}/leads/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ext = format === "xlsx" ? "xlsx" : "csv";
      a.href = url;
      a.download = `richieste_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Esportazione ${ext.toUpperCase()} completata`);
    } catch (e) { toast.error("Errore durante l'esportazione"); }
    finally { setExporting(false); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await axios.post(`${API}/email/test`);
      if (data.status === "delivered") toast.success(`Email di test consegnata a ${data.to}`);
      else if (data.status === "accepted") toast.success(`Email di test accettata dal provider (${data.to})`);
      else if (data.status === "blocked_sandbox") toast.message("Email accettata dal provider ma non consegnata (modalità sandbox).");
      else if (data.status === "rate_limited") toast.warning("Limite invii email raggiunto. Riprova tra qualche minuto.");
      else if (data.status === "not_configured") toast.error("Notifiche email non configurate");
      else toast.error(data.error || "Invio di test non riuscito");
    } catch (e) { toast.error("Errore durante l'invio di test"); }
    finally { setTestingEmail(false); }
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <Link to="/crm/dashboard" data-testid="crm-to-dashboard" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-deep transition-colors">
              <BarChart3 size={15} /> Dashboard
            </Link>
            <Link to="/crm/galleria" data-testid="crm-to-gallery" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink/70 hover:text-navy hover:border-navy/30 transition-colors">
              <ImageIcon size={15} /> <span className="hidden sm:inline">Galleria</span>
            </Link>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-navy">
              <ArrowLeft size={16} /> Sito
            </Link>
            <button onClick={logout} data-testid="crm-logout" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-2 text-sm font-semibold text-ink/70 hover:text-red-600 hover:border-red-200 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-extrabold text-3xl text-navy">Richieste (CRM)</h1>
            <p className="text-ink/55 mt-1">{leads.length} richieste</p>
          </div>
          <button onClick={load} data-testid="crm-refresh" className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-gray-50 transition-colors">
            <RefreshCw size={15} /> Aggiorna
          </button>
        </div>

        {/* Email error alert banner */}
        {emailErrors > 0 && (
          <div data-testid="crm-email-alert" className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-center gap-2.5 flex-1">
              <AlertTriangle size={18} className="text-red-600 shrink-0" />
              <p className="text-sm text-red-700">
                <span className="font-semibold">{emailErrors} notifich{emailErrors === 1 ? "a" : "e"} email fallit{emailErrors === 1 ? "a" : "e"}</span> — l'ultimo invio non è riuscito. Riprova dal dettaglio del lead.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setFEmail("failed"); setView("active"); }} data-testid="crm-email-alert-view" className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-red-700">
                Vedi richieste
              </button>
              {isAdmin && (
                <button onClick={sendTestEmail} disabled={testingEmail} data-testid="crm-email-test" className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-3.5 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60">
                  {testingEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Invia email di test
                </button>
              )}
            </div>
          </div>
        )}

        {/* Old trash warning banner */}
        {trashOldCount > 0 && view !== "trash" && (
          <div data-testid="crm-trash-old-alert" className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex items-center gap-2.5 flex-1">
              <Clock size={18} className="text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{trashOldCount} richiest{trashOldCount === 1 ? "a" : "e"} nel cestino da oltre 30 giorni</span> — decidi tu se eliminarle definitivamente (nessuna cancellazione automatica).
              </p>
            </div>
            <button onClick={() => setView("trash")} data-testid="crm-trash-old-view" className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-amber-700 self-start sm:self-auto">
              <Trash2 size={13} /> Vai al cestino
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="crm-search"
              placeholder="Cerca nome, email, telefono, lavoro..."
              className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-navy"
            />
          </div>
          <select value={fStato} onChange={(e) => setFStato(e.target.value)} data-testid="crm-filter-stato" className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">Tutti gli stati</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fProv} onChange={(e) => setFProv(e.target.value)} data-testid="crm-filter-prov" className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">Tutte le origini</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fPrio} onChange={(e) => setFPrio(e.target.value)} data-testid="crm-filter-prio" className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">Tutte le priorità</option>
            {PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fEmail} onChange={(e) => setFEmail(e.target.value)} data-testid="crm-filter-email" className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">Tutti gli invii email</option>
            {EMAIL_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input
            value={fZona}
            onChange={(e) => setFZona(e.target.value)}
            data-testid="crm-filter-zona"
            placeholder="Zona..."
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none w-[140px]"
          />
          <div className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
            <CalendarDays size={15} className="text-ink/40" />
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="crm-filter-from" className="outline-none bg-transparent text-xs" />
            <span className="text-ink/30">→</span>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="crm-filter-to" className="outline-none bg-transparent text-xs" />
          </div>
        </div>

        {/* Toolbar: view switch + bulk/test actions */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setView((v) => (v === "archived" ? "active" : "archived"))}
            data-testid="crm-toggle-archived"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${view === "archived" ? "bg-navy text-white border-navy" : "bg-white text-navy border-gray-200 hover:bg-gray-50"}`}
          >
            <Archive size={15} /> {view === "archived" ? "Vedi attive" : "Archiviate"}
          </button>
          <button
            onClick={() => setView((v) => (v === "trash" ? "active" : "trash"))}
            data-testid="crm-toggle-trash"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${view === "trash" ? "bg-red-600 text-white border-red-600" : "bg-white text-navy border-gray-200 hover:bg-gray-50"}`}
          >
            <Trash2 size={15} /> {view === "trash" ? "Esci dal cestino" : `Cestino${trashCount > 0 ? ` (${trashCount})` : ""}`}
          </button>

          {(fZona || fFrom || fTo || fStato || fProv || fPrio || fEmail || q) && (
            <button
              onClick={() => { setQ(""); setFStato(""); setFProv(""); setFPrio(""); setFEmail(""); setFZona(""); setFFrom(""); setFTo(""); setFSopralluogo(""); }}
              data-testid="crm-clear-filters"
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-gray-50"
            >
              <X size={15} /> Azzera filtri
            </button>
          )}

          <div className="flex-1" />

          {/* Trash bulk actions */}
          {view === "trash" && isAdmin && selectedIds.size > 0 && (
            <>
              <button onClick={doBulkRestore} disabled={busy} data-testid="crm-bulk-restore" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-deep disabled:opacity-60">
                <RotateCcw size={15} /> Ripristina ({selectedIds.size})
              </button>
              <button onClick={doBulkPermanent} disabled={busy} data-testid="crm-bulk-permanent" className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                <Trash size={15} /> Elimina per sempre ({selectedIds.size})
              </button>
            </>
          )}
          {view === "trash" && isAdmin && trashCount > 0 && (
            <button onClick={doEmptyTrash} disabled={busy} data-testid="crm-empty-trash" className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
              <Trash size={15} /> Svuota cestino
            </button>
          )}

          {/* Active/archived view actions */}
          {view !== "trash" && isAdmin && (
            <button
              onClick={sendTestEmail}
              disabled={testingEmail}
              data-testid="crm-email-test-toolbar"
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-gray-50 disabled:opacity-60"
            >
              {testingEmail ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Test email
            </button>
          )}
          {view !== "trash" && isAdmin && selectedIds.size > 0 && (
            <button
              onClick={doBulkDelete}
              disabled={busy}
              data-testid="crm-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              <Trash2 size={15} /> Elimina selezionati ({selectedIds.size})
            </button>
          )}
          {view !== "trash" && isAdmin && (
            <button
              onClick={doDeleteTest}
              disabled={busy}
              data-testid="crm-delete-test"
              className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 size={15} /> Elimina richieste di test
            </button>
          )}
        </div>

        {/* Quick filters + export */}
        {view !== "trash" && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink/40">Rapidi:</span>
            <button
              onClick={() => setFStato((s) => (s === "Lavoro concluso" ? "" : "Lavoro concluso"))}
              data-testid="crm-quick-clienti"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${fStato === "Lavoro concluso" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"}`}
            >
              <Users size={15} /> Clienti
            </button>
            <button
              onClick={() => setFSopralluogo((s) => (s === "due" ? "" : "due"))}
              data-testid="crm-quick-sopralluoghi"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${fSopralluogo === "due" ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-700 border-orange-200 hover:bg-orange-50"}`}
            >
              <Clock size={15} /> Sopralluoghi da gestire{sopralluoghiDue > 0 ? ` (${sopralluoghiDue})` : ""}
            </button>

            <div className="flex-1" />

            <button onClick={() => doExport("csv")} disabled={exporting} data-testid="crm-export-csv" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-gray-50 disabled:opacity-60">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <ArrowLeft size={15} className="rotate-90" />} Esporta CSV
            </button>
            <button onClick={() => doExport("xlsx")} disabled={exporting} data-testid="crm-export-xlsx" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <ArrowLeft size={15} className="rotate-90" />} Esporta Excel
            </button>
          </div>
        )}

        {/* Clienti summary */}
        {fStato === "Lavoro concluso" && view !== "trash" && (
          <div data-testid="crm-clienti-summary" className="mt-4 flex flex-wrap items-center gap-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-700/70 font-semibold">Clienti (lavori conclusi)</p>
              <p className="text-2xl font-display font-extrabold text-amber-900" data-testid="crm-clienti-count">{clientsCount}</p>
            </div>
            <div className="h-10 w-px bg-amber-200" />
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-700/70 font-semibold">Fatturato totale</p>
              <p className="text-2xl font-display font-extrabold text-amber-900" data-testid="crm-clienti-revenue">€ {clientsRevenue.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
            </div>
            <p className="text-xs text-amber-700/70 basis-full sm:basis-auto">Solo importi (preventivo) realmente registrati nei lead.</p>
          </div>
        )}

        {/* Sopralluoghi alert banner */}
        {sopralluoghiDue > 0 && fSopralluogo !== "due" && view !== "trash" && (
          <div data-testid="crm-sopralluogo-alert" className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3">
            <div className="flex items-center gap-2.5 flex-1">
              <Clock size={18} className="text-orange-600 shrink-0" />
              <p className="text-sm text-orange-800">
                <span className="font-semibold">{sopralluoghiDue} sopralluogh{sopralluoghiDue === 1 ? "o" : "i"} da gestire</span> — previsti entro 24 ore, oggi o già scaduti.
              </p>
            </div>
            <button onClick={() => setFSopralluogo("due")} data-testid="crm-sopralluogo-view" className="inline-flex items-center gap-1.5 rounded-full bg-orange-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-orange-700 self-start sm:self-auto">
              Gestisci ora
            </button>
          </div>
        )}


        <div className="mt-6 rounded-2xl bg-white border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-navy" /></div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-ink/50">Nessuna richiesta trovata.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface text-left text-ink/60 text-xs uppercase tracking-wider">
                    {isAdmin && (
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          data-testid="crm-select-all"
                          checked={leads.length > 0 && selectedIds.size === leads.length}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 accent-navy cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 font-semibold">Cliente</th>
                    <th className="px-4 py-3 font-semibold">Lavoro</th>
                    <th className="px-4 py-3 font-semibold">Priorità</th>
                    <th className="px-4 py-3 font-semibold">Origine</th>
                    <th className="px-4 py-3 font-semibold">Stato</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`crm-lead-${l.id}`} className={`hover:bg-surface/60 cursor-pointer ${selectedIds.has(l.id) ? "bg-navy/[0.04]" : ""}`} onClick={() => setSelected(l)}>
                      {isAdmin && (
                        <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`crm-select-${l.id}`}
                            checked={selectedIds.has(l.id)}
                            onChange={() => toggleSelect(l.id)}
                            className="h-4 w-4 rounded border-gray-300 accent-navy cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 align-top">
                        <p className="font-semibold text-navy flex items-center gap-1.5">{l.nome}
                          {l.stato === "Lavoro concluso" && l.protected && <span data-testid={`crm-client-badge-${l.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Cliente</span>}
                          {l.protected && <span data-testid={`crm-protected-badge-${l.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold"><ShieldCheck size={11} /> Protetto</span>}
                          {l.archived && <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-[10px] font-semibold">Archiviata</span>}
                        </p>
                        <p className="text-xs text-ink/45">{new Date(l.created_at).toLocaleString("it-IT")}</p>
                        {l.zona && <p className="mt-1 text-xs text-ink/55 flex items-center gap-1"><MapPin size={12} /> {l.zona}</p>}
                        {l.email && <p className="mt-0.5 text-xs text-ink/55 flex items-center gap-1 truncate max-w-[220px]"><Mail size={12} /> {l.email}</p>}
                        {l.telefono && <p className="mt-0.5 text-xs text-ink/55 flex items-center gap-1"><Phone size={12} /> {l.telefono}</p>}
                        {(() => { const sb = sopralluogoStatus(l.data_sopralluogo, l.stato); return sb ? <span data-testid={`crm-sopralluogo-badge-${l.id}`} className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${sb.cls}`}><Clock size={10} /> {sb.label}</span> : null; })()}
                      </td>
                      <td className="px-4 py-3 align-top max-w-[220px]">
                        <p className="font-medium text-ink">{l.tipo_lavoro}</p>
                        <p className="text-xs text-ink/45">{[l.dimensione, l.urgenza].filter(Boolean).join(" · ")}</p>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${PRIORITY_COLORS[l.priorita]}`}>{l.priorita} · {l.lead_score}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-block rounded-full bg-gray-100 px-2.5 py-1 text-xs text-ink/70">{l.provenienza}</span>
                      </td>
                      <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={l.stato}
                          onChange={(e) => patch(l.id, { stato: e.target.value })}
                          data-testid={`crm-status-${l.id}`}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold border-0 cursor-pointer ${STATE_COLORS[l.stato] || "bg-gray-100 text-ink"}`}
                        >
                          {states.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <EmailBadge status={l.email_status} />
                      </td>
                      <td className="px-4 py-3 align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          {view === "trash" ? (
                            <>
                              {isAdmin && (
                                <button onClick={() => doRestoreSingle(l)} data-testid={`crm-restore-${l.id}`} title="Ripristina" className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink/50 hover:bg-navy/5 hover:text-navy">
                                  <RotateCcw size={15} />
                                </button>
                              )}
                              {isAdmin && (
                                <button onClick={() => doPermanentSingle(l)} data-testid={`crm-permanent-${l.id}`} title="Elimina definitivamente" className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink/50 hover:bg-red-50 hover:text-red-600">
                                  <Trash size={15} />
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <button onClick={() => setSelected(l)} data-testid={`crm-open-${l.id}`} className="rounded-lg px-2 py-1 text-navy text-xs font-semibold hover:bg-navy/5">Apri</button>
                              {isAdmin && (
                                <button onClick={() => doProtect(l, !l.protected)} data-testid={`crm-protect-${l.id}`} title={l.protected ? "Rimuovi protezione" : "Proteggi"} className={`h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-emerald-50 ${l.protected ? "text-emerald-600" : "text-ink/50 hover:text-emerald-600"}`}>
                                  {l.protected ? <ShieldCheck size={15} /> : <Shield size={15} />}
                                </button>
                              )}
                              <button onClick={() => doArchive(l, !l.archived)} data-testid={`crm-archive-${l.id}`} title={l.archived ? "Ripristina" : "Archivia"} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink/50 hover:bg-gray-100 hover:text-navy">
                                {l.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                              </button>
                              {isAdmin && (
                                <button onClick={() => doDeleteSingle(l)} disabled={l.protected} data-testid={`crm-delete-${l.id}`} title={l.protected ? "Lead protetto" : "Elimina"} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink/50 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <LeadDrawer lead={selected} onClose={() => setSelected(null)} patch={patch} apply={applyUpdate} states={states} isAdmin={isAdmin} view={view} onArchive={doArchive} onDelete={doDeleteSingle} onProtect={doProtect} onRestore={doRestoreSingle} onPermanent={doPermanentSingle} />
      <ConfirmDialog state={confirm} onOpenChange={(o) => !o && closeConfirm()} />
    </div>
  );
}

function LeadDrawer({ lead, onClose, patch, apply, states, isAdmin, view, onArchive, onDelete, onProtect, onRestore, onPermanent }) {
  const [form, setForm] = useState({ note: "", data_sopralluogo: "", costo_preventivo: "", valore_stimato: "", priorita: "Media", stato_preventivo: "Da preparare", data_preventivo: "" });
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [copied, setCopied] = useState("");
  const [waText, setWaText] = useState("");
  const [emailText, setEmailText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [tono, setTono] = useState("professionale");
  const [template, setTemplate] = useState("prima_risposta");
  const [regen, setRegen] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [resending, setResending] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  useEffect(() => {
    if (lead) {
      setForm({
        note: lead.note || "",
        data_sopralluogo: lead.data_sopralluogo || "",
        costo_preventivo: lead.costo_preventivo ?? "",
        valore_stimato: lead.valore_stimato ?? "",
        priorita: lead.priorita,
        stato_preventivo: lead.stato_preventivo || "Da preparare",
        data_preventivo: lead.data_preventivo || "",
      });
      setWaText(lead.ai?.risposta_whatsapp || "");
      setEmailText(lead.ai?.risposta_email || "");
      setDirty(false);
    }
  }, [lead]);

  const logActivity = async (type, detail = "") => {
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/activity`, { type, detail });
      apply(data);
    } catch (e) { /* non-blocking */ }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) { toast.error("Scrivi una nota prima di salvare"); return; }
    setSavingNote(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/note`, { text });
      apply(data);
      setNoteText("");
      toast.success("Nota salvata");
    } catch (e) { toast.error("Errore nel salvataggio della nota"); }
    finally { setSavingNote(false); }
  };

  const openWhatsApp = () => {
    const num = normalizeItalianPhone(lead.telefono);
    if (!num) { toast.error("Numero WhatsApp mancante o non valido per questo lead."); return; }
    if (!waText.trim()) { toast.error("La bozza WhatsApp è vuota."); return; }
    window.open(waLinkTo(num, waText), "_blank", "noopener,noreferrer");
    logActivity("whatsapp_opened");
  };

  const openEmail = () => {
    if (!isValidEmail(lead.email)) { toast.error("Email mancante o non valida per questo lead."); return; }
    const a = document.createElement("a");
    a.href = mailtoTo(lead.email, EMAIL_SUBJECT, emailText);
    a.click();
    logActivity("email_opened");
  };

  const copyDraft = (which) => {
    const text = which === "wa" ? waText : emailText;
    Promise.resolve(navigator.clipboard?.writeText(text || ""))
      .then(() => { setCopied(which); setTimeout(() => setCopied(""), 1500); logActivity("text_copied", which); })
      .catch(() => toast.error("Copia non riuscita"));
  };

  const saveDrafts = async () => {
    setSavingDraft(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${lead.id}`, {
        ai_risposta_whatsapp: waText,
        ai_risposta_email: emailText,
      });
      apply(data);
      setDirty(false);
      toast.success("Bozza salvata");
    } catch (e) { toast.error("Errore nel salvataggio bozza"); }
    finally { setSavingDraft(false); }
  };

  const regenerate = async (targets) => {
    if (dirty &&
        !window.confirm("Ci sono modifiche non salvate alla bozza. Rigenerare sovrascrivendo il testo attuale?")) {
      return;
    }
    setRegen(targets.join("+"));
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/ai-draft`, { targets, tono, template }, { timeout: 90000 });
      apply(data);
      if (targets.includes("whatsapp")) setWaText(data.ai?.risposta_whatsapp || "");
      if (targets.includes("email")) setEmailText(data.ai?.risposta_email || "");
      setDirty(false);
      toast.success(data.ai?.draft_ai_generated ? "Bozza rigenerata con AI" : "Bozza rigenerata (regole)");
    } catch (e) { toast.error("Errore nella rigenerazione"); }
    finally { setRegen(""); }
  };

  const resendEmail = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/resend-email`, null, { timeout: 60000 });
      apply(data);
      if (data.email_status === "delivered") toast.success("Email di notifica consegnata");
      else if (data.email_status === "accepted") toast.success("Email di notifica accettata dal provider");
      else if (data.email_status === "blocked_sandbox") toast.message("Email accettata ma non consegnata (modalità sandbox).");
      else if (data.email_status === "rate_limited") toast.warning("Limite invii raggiunto. Riprova tra qualche minuto.");
      else if (data.email_status === "not_configured") toast.error("Notifiche email non configurate");
      else toast.error(data.email_error || "Invio non riuscito");
    } catch (e) { toast.error("Errore durante il reinvio"); }
    finally { setResending(false); }
  };

  const markContacted = async () => {    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/mark-contacted`);
      apply(data);
      toast.success("Lead segnato come contattato");
    } catch (e) { toast.error("Errore"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await patch(lead.id, {
        note: form.note,
        data_sopralluogo: form.data_sopralluogo,
        costo_preventivo: form.costo_preventivo === "" ? null : parseFloat(form.costo_preventivo),
        valore_stimato: form.valore_stimato === "" ? null : parseFloat(form.valore_stimato),
        priorita: form.priorita,
        stato_preventivo: form.stato_preventivo,
        data_preventivo: form.data_preventivo,
      });
      toast.success("Salvato");
    } finally { setSaving(false); }
  };

  const aiAnalyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/ai-analyze`, null, { timeout: 90000 });
      apply(data);
      setWaText(data.ai?.risposta_whatsapp || "");
      setEmailText(data.ai?.risposta_email || "");
      setDirty(false);
      toast.success(data.ai?.ai_generated ? "Analisi AI completata" : "Analisi (regole) completata");
    } catch (e) {
      toast.error("Errore analisi AI");
    } finally { setAnalyzing(false); }
  };

  const ai = lead?.ai;

  return (
    <AnimatePresence>
      {lead && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/40 z-30" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            data-testid="crm-drawer"
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-40 shadow-2xl overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-display font-extrabold text-lg text-navy flex items-center gap-2">{lead.nome}
                  {lead.stato === "Lavoro concluso" && lead.protected && <span data-testid="crm-drawer-client-badge" className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Cliente</span>}
                  {lead.protected && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold"><ShieldCheck size={11} /> Protetto</span>}
                </h2>
                <p className="text-xs text-ink/45">{new Date(lead.created_at).toLocaleString("it-IT")}</p>
              </div>
              <button onClick={onClose} data-testid="crm-drawer-close" className="h-9 w-9 rounded-full hover:bg-surface flex items-center justify-center"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-6">
              {/* Contatti */}
              <div className="space-y-2 text-sm">
                <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-ink/75 hover:text-navy"><Mail size={15} /> {lead.email}</a>
                {lead.telefono && <a href={`tel:${lead.telefono}`} className="flex items-center gap-2 text-ink/75 hover:text-navy"><Phone size={15} /> {lead.telefono}</a>}
                {lead.zona && <p className="flex items-center gap-2 text-ink/75"><MapPin size={15} /> {lead.zona}</p>}
              </div>

              {/* Stato notifica email */}
              <div className="rounded-xl border border-gray-200 p-4" data-testid="crm-email-status-card">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">Notifica email (admin)</p>
                  <EmailBadge status={lead.email_status} />
                </div>
                {(lead.email_status === "error" || lead.email_status === "failed") && lead.email_error && (
                  <p className="mt-2 text-xs text-red-600" data-testid="crm-email-error-msg">{lead.email_error}</p>
                )}
                {lead.email_status === "rate_limited" && (
                  <p className="mt-2 text-xs text-yellow-700" data-testid="crm-email-ratelimit-msg">Limite invii del servizio email raggiunto (429). È temporaneo: riprova tra qualche minuto. La richiesta è comunque salvata.</p>
                )}
                {lead.email_status === "blocked_sandbox" && (
                  <p className="mt-2 text-xs text-orange-600" data-testid="crm-email-sandbox-msg">Accettata dal provider ma non consegnata: il servizio email è in modalità test e recapita solo all'indirizzo verificato. Sarà consegnata una volta attivata la modalità produzione.</p>
                )}
                {lead.email_status === "not_configured" && (
                  <p className="mt-2 text-xs text-ink/55">Le notifiche email non sono configurate. La richiesta resta comunque salvata nel CRM.</p>
                )}
                {lead.email_last_attempt_at && (
                  <p className="mt-2 text-xs text-ink/45">Ultimo tentativo: {new Date(lead.email_last_attempt_at).toLocaleString("it-IT")}</p>
                )}
                <button
                  onClick={resendEmail}
                  disabled={resending || lead.email_status === "sending"}
                  data-testid="crm-resend-email"
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-60"
                >
                  {resending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Riprova invio
                </button>
                {lead.customer_email_status && (
                  <div className="mt-4 pt-3 border-t border-gray-100" data-testid="crm-customer-email-status">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">Conferma al cliente</p>
                      <EmailBadge status={lead.customer_email_status} />
                    </div>
                    {(lead.customer_email_status === "error" || lead.customer_email_status === "failed") && lead.customer_email_error && (
                      <p className="mt-2 text-xs text-red-600" data-testid="crm-customer-email-error">{lead.customer_email_error}</p>
                    )}
                    {lead.customer_email_status === "rate_limited" && (
                      <p className="mt-2 text-xs text-yellow-700" data-testid="crm-customer-email-ratelimit">Limite invii del servizio email raggiunto (429). È temporaneo: verrà rispedita al prossimo tentativo. La richiesta è comunque salvata.</p>
                    )}
                    {lead.customer_email_status === "blocked_sandbox" && (
                      <p className="mt-2 text-xs text-orange-600" data-testid="crm-customer-email-sandbox">Accettata dal provider ma non ancora consegnata al cliente: in attesa dell'attivazione della modalità produzione email. La richiesta è salvata regolarmente.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Lavoro */}
              <div className="rounded-xl bg-surface p-4">
                <p className="font-semibold text-navy">{lead.tipo_lavoro}</p>
                <p className="text-xs text-ink/55 mt-0.5">{[lead.dimensione, lead.urgenza].filter(Boolean).join(" · ")}</p>
                {lead.descrizione && <p className="mt-2 text-sm text-ink/70">{lead.descrizione}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PRIORITY_COLORS[lead.priorita]}`}>{lead.priorita} · score {lead.lead_score}</span>
                </div>
              </div>

              {/* AI Assistant */}
              <div className="rounded-xl border border-navy/15 bg-navy/[0.03] p-4">
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-navy flex items-center gap-2"><Bot size={18} className="text-brand-orange" /> Assistente AI</p>
                  <button onClick={aiAnalyze} disabled={analyzing} data-testid="crm-ai-analyze" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-2 text-xs font-semibold text-white hover:bg-navy-deep transition-colors disabled:opacity-70">
                    {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {ai ? "Rianalizza" : "Analizza"}
                  </button>
                </div>

                {!ai ? (
                  <p className="mt-3 text-sm text-ink/55">Nessuna analisi ancora. Clicca "Analizza" per generare qualificazione, riepilogo e bozze di risposta.</p>
                ) : (
                  <div className="mt-4 space-y-4">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${ai.ai_generated ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {ai.ai_generated ? "Generato da AI" : "Logica a regole (fallback)"}
                    </span>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-white border border-gray-100 p-2.5 text-center">
                        <p className="text-lg font-display font-extrabold text-navy">{ai.conversion_probability}%</p>
                        <p className="text-[10px] text-ink/50 flex items-center justify-center gap-0.5"><TrendingUp size={10} /> Conversione</p>
                      </div>
                      <div className="rounded-lg bg-white border border-gray-100 p-2.5 text-center">
                        <p className="text-lg font-display font-extrabold text-navy">{ai.lead_score}</p>
                        <p className="text-[10px] text-ink/50">Punteggio</p>
                      </div>
                      <div className="rounded-lg bg-white border border-gray-100 p-2.5 text-center">
                        <p className="text-sm font-display font-extrabold text-navy mt-1">{ai.complessita}</p>
                        <p className="text-[10px] text-ink/50">Complessità</p>
                      </div>
                    </div>

                    <div className="text-sm">
                      <p className="text-ink/45 text-xs uppercase tracking-wide">Categoria</p>
                      <p className="text-ink font-medium">{ai.categoria}</p>
                    </div>

                    <div className="rounded-lg bg-white border border-gray-100 p-3">
                      <p className="text-ink/45 text-xs uppercase tracking-wide mb-1">Riepilogo</p>
                      <p className="text-sm text-ink/80" data-testid="crm-ai-summary">{ai.riepilogo}</p>
                    </div>

                    <div className="text-xs text-ink/60">
                      <span className="font-semibold text-navy">Motivazione:</span> {ai.motivazione}
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <Clock size={13} className="text-brand-orange" /> Tempo di risposta consigliato: <span className="font-semibold text-navy">{ai.tempo_stimato_risposta}</span>
                    </div>

                    {/* Dati estratti */}
                    {ai.dati_estratti && (
                      <div>
                        <p className="text-ink/45 text-xs uppercase tracking-wide mb-2">Dati estratti</p>
                        <div className="grid grid-cols-2 gap-1.5 text-xs">
                          {[
                            ["Immobile", ai.dati_estratti.tipologia_immobile],
                            ["Locali", ai.dati_estratti.numero_locali],
                            ["Bagno", ai.dati_estratti.presenza_bagno === true ? "Sì" : ai.dati_estratti.presenza_bagno === false ? "No" : ""],
                            ["Cucina", ai.dati_estratti.presenza_cucina === true ? "Sì" : ai.dati_estratti.presenza_cucina === false ? "No" : ""],
                            ["Superfici", ai.dati_estratti.superfici],
                            ["Materiali", (ai.dati_estratti.materiali || []).join(", ")],
                          ].filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="rounded-lg bg-white border border-gray-100 px-2.5 py-1.5"><span className="text-ink/45">{k}: </span><span className="text-ink/80">{v}</span></div>
                          ))}
                        </div>
                        {(ai.dati_estratti.lavori_richiesti || []).length > 0 && (
                          <p className="mt-2 text-xs text-ink/70"><span className="text-ink/45">Lavori: </span>{ai.dati_estratti.lavori_richiesti.join(", ")}</p>
                        )}
                      </div>
                    )}

                    {/* Suggerimenti */}
                    {(ai.suggerimenti || []).length > 0 && (
                      <div>
                        <p className="text-ink/45 text-xs uppercase tracking-wide mb-2 flex items-center gap-1"><Lightbulb size={12} /> Suggerimenti operativi</p>
                        <div className="flex flex-wrap gap-1.5">
                          {ai.suggerimenti.map((s) => (
                            <span key={s} className="rounded-full bg-brand-orange/10 text-brand-orange px-2.5 py-1 text-xs font-medium">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Draft replies — editable + one-click actions */}
                    <div className="rounded-lg border border-gray-200 p-3 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-navy text-sm flex items-center gap-1.5"><Send size={14} className="text-brand-orange" /> Bozze di risposta</p>
                        {ai.manual_edited && <span className="text-[10px] rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">Modificata manualmente</span>}
                      </div>

                      {/* Regenerate controls */}
                      <div className="flex flex-wrap items-center gap-2">
                        <select value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="crm-draft-template" className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs">
                          {TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                        <select value={tono} onChange={(e) => setTono(e.target.value)} data-testid="crm-draft-tono" className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs capitalize">
                          {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button onClick={() => regenerate(["whatsapp", "email"])} disabled={!!regen} data-testid="crm-regen-both" className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-deep disabled:opacity-60">
                          {regen === "whatsapp+email" ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Rigenera entrambe
                        </button>
                      </div>

                      {/* WhatsApp draft */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-ink/45 text-xs uppercase tracking-wide flex items-center gap-1"><MessageCircle size={12} /> Bozza WhatsApp</p>
                          <button onClick={() => regenerate(["whatsapp"])} disabled={!!regen} data-testid="crm-regen-wa" className="text-xs text-navy hover:underline inline-flex items-center gap-1">
                            {regen === "whatsapp" ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />} Rigenera
                          </button>
                        </div>
                        <textarea
                          value={waText}
                          onChange={(e) => { setWaText(e.target.value); setDirty(true); }}
                          data-testid="crm-draft-wa-text"
                          rows={3}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:border-navy outline-none"
                          placeholder="Bozza WhatsApp..."
                        />
                        <div className="mt-1.5 flex gap-2">
                          <button onClick={openWhatsApp} data-testid="crm-open-whatsapp" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1ebe5b]">
                            <Send size={13} /> Apri in WhatsApp
                          </button>
                          <button onClick={() => copyDraft("wa")} data-testid="crm-copy-whatsapp" className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-semibold text-navy hover:bg-gray-50">
                            {copied === "wa" ? <Check size={13} /> : <Copy size={13} />} Copia
                          </button>
                        </div>
                      </div>

                      {/* Email draft */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-ink/45 text-xs uppercase tracking-wide flex items-center gap-1"><Mail size={12} /> Bozza Email</p>
                          <button onClick={() => regenerate(["email"])} disabled={!!regen} data-testid="crm-regen-email" className="text-xs text-navy hover:underline inline-flex items-center gap-1">
                            {regen === "email" ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />} Rigenera
                          </button>
                        </div>
                        <textarea
                          value={emailText}
                          onChange={(e) => { setEmailText(e.target.value); setDirty(true); }}
                          data-testid="crm-draft-email-text"
                          rows={5}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:border-navy outline-none"
                          placeholder="Bozza email..."
                        />
                        <div className="mt-1.5 flex gap-2">
                          <button onClick={openEmail} data-testid="crm-open-email" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-navy px-3 py-2 text-xs font-semibold text-white hover:bg-navy-deep">
                            <Send size={13} /> Apri in Email
                          </button>
                          <button onClick={() => copyDraft("em")} data-testid="crm-copy-email" className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 text-xs font-semibold text-navy hover:bg-gray-50">
                            {copied === "em" ? <Check size={13} /> : <Copy size={13} />} Copia
                          </button>
                        </div>
                      </div>

                      {dirty && (
                        <button onClick={saveDrafts} disabled={savingDraft} data-testid="crm-save-draft" className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-brand-orange text-brand-orange px-3 py-2 text-xs font-semibold hover:bg-brand-orange/5">
                          {savingDraft ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salva modifiche bozza
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Photos */}
              {lead.photos?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/50 mb-2 flex items-center gap-1"><ImageIcon size={13} /> Foto ({lead.photos.length})</p>
                  <div className="grid grid-cols-4 gap-2">
                    {lead.photos.map((p, i) => <img key={i} src={p.data_url} alt="" className="aspect-square rounded-lg object-cover border border-gray-200" />)}
                  </div>
                </div>
              )}

              {/* Attribution */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50 mb-2">Attribuzione</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    ["Origine", lead.provenienza], ["UTM Source", lead.utm_source], ["UTM Medium", lead.utm_medium],
                    ["Campagna", lead.utm_campaign], ["Term", lead.utm_term], ["Content", lead.utm_content],
                    ["Referrer", lead.referrer], ["Landing", lead.landing_page],
                    ["Browser", lead.browser], ["Device", lead.device], ["OS", lead.os],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-surface px-2.5 py-1.5"><span className="text-ink/45">{k}: </span><span className="text-ink/80 break-all">{v}</span></div>
                  ))}
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">Gestione</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Priorità</span>
                    <select value={form.priorita} onChange={(e) => setForm({ ...form, priorita: e.target.value })} data-testid="crm-edit-priorita" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                      {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Data sopralluogo</span>
                    <input type="date" value={form.data_sopralluogo} onChange={(e) => setForm({ ...form, data_sopralluogo: e.target.value })} data-testid="crm-edit-sopralluogo" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  </label>
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Importo preventivo (€)</span>
                    <input type="number" value={form.costo_preventivo} onChange={(e) => setForm({ ...form, costo_preventivo: e.target.value })} data-testid="crm-edit-costo" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  </label>
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Valore lavoro (€)</span>
                    <input type="number" value={form.valore_stimato} onChange={(e) => setForm({ ...form, valore_stimato: e.target.value })} data-testid="crm-edit-valore" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  </label>
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Stato preventivo</span>
                    <select value={form.stato_preventivo} onChange={(e) => setForm({ ...form, stato_preventivo: e.target.value })} data-testid="crm-edit-stato-preventivo" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                      {["Da preparare", "Inviato", "Accettato", "Rifiutato"].map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="text-ink/60 text-xs">Data invio preventivo</span>
                    <input type="date" value={form.data_preventivo} onChange={(e) => setForm({ ...form, data_preventivo: e.target.value })} data-testid="crm-edit-data-preventivo" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  </label>
                </div>
                <label className="text-sm block">
                  <span className="text-ink/60 text-xs">Note interne</span>
                  <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="crm-edit-note" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none" placeholder="Appunti sul cliente..." />
                </label>
                <button onClick={save} disabled={saving} data-testid="crm-save" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-brand-orange-dark transition-colors disabled:opacity-70">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salva
                </button>
                <button onClick={markContacted} data-testid="crm-mark-contacted" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                  <CheckCheck size={16} /> Segna come contattato
                </button>
                {view === "trash" ? (
                  isAdmin && (
                    <div className="flex gap-2">
                      <button onClick={() => onRestore(lead)} data-testid="crm-drawer-restore" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-4 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                        <RotateCcw size={16} /> Ripristina
                      </button>
                      <button onClick={() => onPermanent(lead)} data-testid="crm-drawer-permanent" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors">
                        <Trash size={16} /> Elimina per sempre
                      </button>
                    </div>
                  )
                ) : (
                  <>
                    {isAdmin && (
                      <button onClick={() => onProtect(lead, !lead.protected)} data-testid="crm-drawer-protect" className={`w-full inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors ${lead.protected ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                        {lead.protected ? <><ShieldCheck size={16} /> Protetto — rimuovi protezione</> : <><Shield size={16} /> Proteggi (non eliminabile)</>}
                      </button>
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => onArchive(lead, !lead.archived)} data-testid="crm-drawer-archive" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-ink/70 hover:bg-gray-50 transition-colors">
                        {lead.archived ? <><ArchiveRestore size={16} /> Ripristina</> : <><Archive size={16} /> Archivia</>}
                      </button>
                      {isAdmin && (
                        <button onClick={() => onDelete(lead)} disabled={lead.protected} data-testid="crm-drawer-delete" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash2 size={16} /> Elimina
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Timeline (stati) */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50 mb-3 flex items-center gap-1"><Clock size={13} /> Storico stati</p>
                <ol className="relative border-l border-gray-200 ml-1.5 space-y-4">
                  {(lead.history || []).map((h, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-brand-orange" />
                      <p className="text-sm font-semibold text-navy">{h.stato}</p>
                      <p className="text-xs text-ink/45">{new Date(h.at).toLocaleString("it-IT")}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Note rapide */}
              <div data-testid="crm-notes-section">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50 mb-3 flex items-center gap-1"><Save size={13} /> Note</p>
                <div className="flex flex-col gap-2">
                  <textarea
                    rows={2}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    data-testid="crm-note-input"
                    placeholder="Aggiungi una nota rapida..."
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none outline-none focus:border-navy"
                  />
                  <button onClick={addNote} disabled={savingNote || !noteText.trim()} data-testid="crm-note-save" className="self-end inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-deep transition-colors disabled:opacity-50">
                    {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Aggiungi nota
                  </button>
                </div>
                {(lead.activity || []).some((a) => a.type === "note") && (
                  <ul className="mt-4 relative border-l border-gray-200 ml-1.5 space-y-4" data-testid="crm-notes-list">
                    {[...(lead.activity || [])].filter((a) => a.type === "note").reverse().map((a, i) => (
                      <li key={i} className="ml-4">
                        <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-navy" />
                        <p className="text-sm text-ink/85 whitespace-pre-wrap">{a.detail}</p>
                        <p className="text-xs text-ink/45">{a.author ? `${a.author} · ` : ""}{new Date(a.at).toLocaleString("it-IT")}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>


              {/* Activity feed */}
              {(lead.activity || []).some((a) => a.type !== "note") && (
                <div className="pb-24 sm:pb-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink/50 mb-3">Azioni registrate</p>
                  <ul className="space-y-2" data-testid="crm-activity-feed">
                    {[...(lead.activity || [])].filter((a) => a.type !== "note").reverse().map((a, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span className="text-ink/75">{ACTIVITY_LABELS[a.type] || a.type}{a.detail ? ` · ${a.detail}` : ""}</span>
                        <span className="text-ink/40">{new Date(a.at).toLocaleString("it-IT")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Mobile sticky action bar */}
            <div className="sm:hidden sticky bottom-0 inset-x-0 bg-white border-t border-gray-100 px-3 py-2 flex gap-2">
              <button onClick={openWhatsApp} data-testid="crm-mobile-whatsapp" className="flex-1 inline-flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-semibold text-[#25D366]"><MessageCircle size={18} /> WhatsApp</button>
              <button onClick={openEmail} data-testid="crm-mobile-email" className="flex-1 inline-flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-semibold text-navy"><Mail size={18} /> Email</button>
              <button onClick={() => copyDraft("wa")} data-testid="crm-mobile-copy" className="flex-1 inline-flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-semibold text-ink/70"><Copy size={18} /> Copia</button>
              <button onClick={markContacted} data-testid="crm-mobile-contacted" className="flex-1 inline-flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-semibold text-brand-orange"><CheckCheck size={18} /> Contattato</button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
